import { Tooltip } from "@mui/material";
import { opcoesBrfMapper } from "core/utils/elegivelBrf.ts";
import { motion } from "framer-motion";
import { memo, useMemo } from "react";
import IndicadorComLoading from "../../../../components/IndicadorComLoading/IndicadorComLoading.tsx";
import { useEmbarqueDependencias } from "../../../../contexts/EmbarqueDependenciasContext.tsx";
import IconButton from "../../../../core/components/IconButton.tsx";
import Separator from "../../../../core/components/Separator.tsx";
import Checkbox from "../../../../core/data-entry/Checkbox.tsx";
import SelectInputField from "../../../../core/data-entry/SelectInputField.tsx";
import { TipoCarroceria } from "../../../../models/TipoCarroceria.ts";
import { DisponibilidadeFrotaFiltros } from "../DisponibilidadeFrota.tsx";
import "./FiltrosDisponibilidadeFrota.css";

interface FiltrosDisponibilidadeFrotaProps {
	buscarDisponibilidadeFrota: () => void;
	handleClearFiltro: () => void;
	filtros: DisponibilidadeFrotaFiltros;
	setFiltros: (patch: Partial<DisponibilidadeFrotaFiltros>) => void;
	loading: boolean;
	qtVeiculos?: number;
}

function FiltrosDisponibilidadeFrota({
	buscarDisponibilidadeFrota,
	handleClearFiltro,
	filtros,
	setFiltros,
	loading,
	qtVeiculos = 0,
}: FiltrosDisponibilidadeFrotaProps) {
	const { tipoCarroceria } = useEmbarqueDependencias();

	const tipoCarroceriaSelecionada = useMemo(() => {
		const tipo = tipoCarroceria.find(
			(item) => item.id === filtros.tipoCarroceriaId
		);
		return tipo ? { title: tipo.nome, obj: tipo } : null;
	}, [tipoCarroceria, filtros.tipoCarroceriaId]);

	const options = useMemo(
		() => [
			{
				key: 1,
				label: "Sem viagem agendada/em andamento",
				checked: filtros.viagemMarcada === 1,
				onCheck: () =>
					setFiltros({
						viagemMarcada: filtros.viagemMarcada === 1 ? null : 1,
					}),
			},
			{
				key: 2,
				label: "Com viagem agendada",
				checked: filtros.viagemMarcada === 2,
				onCheck: () =>
					setFiltros({
						viagemMarcada: filtros.viagemMarcada === 2 ? null : 2,
					}),
			},
		],
		[filtros.viagemMarcada, setFiltros]
	);

	const tipoCarroceriaOptions = useMemo(() => {
		return tipoCarroceria.map(
			(item) => ({
				title: item.nome,
				obj: item,
			})
		)
	}, [tipoCarroceria]);

	const elegivelBrfOptions = useMemo(() => {
		return [
			{ title: "Todos", obj: 0 },
			...opcoesBrfMapper.map((option) => ({
				title: option.label,
				obj: option.value,
			})),
		]
	}, []);

	return (
		<motion.div
			onClick={(e) => e.stopPropagation()}
			initial={{ opacity: 0 }}
			exit={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.3 }}
			className="modal-filtros-frota-container"
		>
			<Separator size="md" />
			<IndicadorComLoading
				loading={loading}
				label="Número de Veículos: "
				value={qtVeiculos}
			/>
			<Separator size="md" />
			<div className="filtros-frota-options-container">
				<div className="filtros-frota-select-boxes-container">
					<div className="modal-filtros-frota-select-boxes-container">
						<p className="modal-filtros-viagens-select-boxes-title">Viagem marcada</p>
						<Separator size="xs" />
						<div className="modal-filtros-frota-checkboxes">
							{options.map((option) => (
								<motion.div
									key={option.key}
									className="modal-filtros-viagens-checkbox"
									initial={{ opacity: 0 }}
									animate={{ opacity: option.checked ? 1 : 0.5 }}
									whileHover={{ opacity: 1 }}
									transition={{ duration: 0.3 }}
									onClick={option.onCheck}
								>
									<Checkbox checked={option.checked} onCheck={option.onCheck} />
									<p>{option.label}</p>
								</motion.div>
							))}
						</div>
					</div>
				</div>


				<div className="modal-filtros-frota-selects">
					<div className="filtro-frota-inputs-wrapper">
						<div className="filtro-data-container coluna-emb" >
							<SelectInputField
								label="Tipo de carroceria:"
								value={tipoCarroceriaSelecionada?.title || ""}
								disabled={loading}
								onChange={(obj) => {
									setFiltros({
										tipoCarroceriaId: (obj.obj as TipoCarroceria).id,
									})
								}
								}
								placeholder="Selecione o tipo:"
								options={tipoCarroceriaOptions}
								onClear={() => setFiltros({ tipoCarroceriaId: 0 })}
							/>
						</div>

						<SelectInputField
							label="Elegibilidade BRF:"
							value={filtros.elegibilidadeBrf === 0 ? "Todos" : elegivelBrfOptions.find(opt => opt.obj === filtros.elegibilidadeBrf)?.title || ""}
							disabled={loading}
							onChange={(obj) => {
								setFiltros({
									elegibilidadeBrf: (obj.obj as number),
								})
							}}
							placeholder="Selecione a elegibilidade BRF:"
							options={elegivelBrfOptions}
						/>
					</div>
					<div className="filtro-frota-inputs-wrapper">
						<div className="filtro-data-disponibilidade">
							<p className="input-label-emb">Dias sem carregar:</p>
							<input
								name="diasSemCarregar"
								type="number"
								className="input-emb"
								style={{ width: "190px" }}
								value={filtros.diasSemCarregar ?? ""}
								onChange={(e) => {
									const v = e.target.value;
									setFiltros({ diasSemCarregar: v === "" ? null : Number(v) });
								}}
							/>
						</div>

						<div className="filtro-data-disponibilidade">
							<p className="input-label-emb">Eixos:</p>
							<input
								name="eixos"
								type="number"
								className="input-emb"
								style={{ width: "190px" }}
								value={filtros.eixos}
								onChange={(e) => {
									const v = e.target.value;
									setFiltros({ eixos: v === "" ? 0 : Number(v) });
								}}
							/>
						</div>
					</div>
					<div className="filtro-data-disponibilidade">
						<p className="input-label-emb">Data próxima viagem:</p>
						<input
							name="dataProximaViagem"
							type="date"
							className="input-emb"
							style={{ width: "190px" }}
							value={filtros.dataProximaViagem}
							onChange={(e) => setFiltros({ dataProximaViagem: e.target.value })}
						/>
					</div>
				</div>
				<motion.div
					transition={{ duration: 0.3 }}
					className="modal-filtros-frota-botoes-container"
				>
					<motion.div
						initial={{ rotate: 0 }}
						whileHover={{ rotate: 90 }}
						transition={{ duration: 0.2, ease: "easeInOut" }}
						className="select-field-chevron"
					>
						<Tooltip title="Pesquisar">
							<IconButton
								icon="Search"
								theme="dark"
								size="xl"
								borderColor="none"
								backgroundColor="#FFFFFF"
								hoverColor="#CCC"
								onClick={() => buscarDisponibilidadeFrota()}
							/>
						</Tooltip>
					</motion.div>
					<motion.div
						initial={{ rotate: 0 }}
						whileHover={{ rotate: 90 }}
						transition={{ duration: 0.2, ease: "easeInOut" }}
						className="select-field-chevron"
					>
						<Tooltip title='Limpar Filtros'>
							<IconButton
								icon="X"
								size="xl"
								borderColor="none"
								onClick={handleClearFiltro}
								backgroundColor="rgba(0, 0, 0, 0)"
								theme="dark"
							/>
						</Tooltip>
					</motion.div>
				</motion.div>
			</div>
		</motion.div>
	);
}

export default memo(FiltrosDisponibilidadeFrota);
